import { useEffect, useReducer, useRef } from 'react';
import { AvatarEditor } from 'beesoft-components';
import AvatarIcon from '../../assets/icons/avatar.svg';
import type { DynamicFormRequiredType } from '../dynamic-form/dynamic-form.enums';
import ErrorLabel from '../labels/error-label';
import { AvatarActionType, avatarReducer } from './avatar.reducer';
import type { FileRef } from '../fields/file-upload/file-upload.component';
import FileUpload from '../fields/file-upload/file-upload.component';
import { useTranslation } from 'react-i18next';

export interface AvatarProps {
  name: string;
  readOnly?: boolean;
  url?: string;
  error?: string;
  label?: string;
  maxFileSize?: number;
  height?: number;
  required?: DynamicFormRequiredType;
  onBeforeFileLoad?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCrop?: (data: string) => void;
  onClose?: () => void;
  onChange?: (data?: string) => void;
}

export default function Avatar({
  name,
  readOnly = false,
  url = '',
  error,
  height = 250,
  onCrop,
  onClose,
  onChange,
  maxFileSize = 1000000,
}: AvatarProps) {
  const { t } = useTranslation();

  const [state, dispatch] = useReducer(avatarReducer, {});

  const initialRender = useRef(true);
  const fileUploadRef = useRef<FileRef>(null);

  useEffect(() => {
    dispatch({ type: AvatarActionType.SetErrorState, errorState: error });
  }, [error]);

  useEffect(() => {
    if (url) {
      dispatch({ type: AvatarActionType.SetSrc, src: url });
    } else {
      resetToDefault();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount to initialize from url prop
  }, []);

  const resetToDefault = () => {
    dispatch({ type: AvatarActionType.SetPreviewAndSrc, preview: AvatarIcon, src: undefined });
  };

  const onFilesChanged = (files: Array<File> | undefined) => {
    if (files && files.length > 0) {
      const file = files[0];

      fileToBase64(file)
        .then((base64String) => {
          if (base64String) {
            dispatch({ type: AvatarActionType.SetSrc, src: base64String });
          }
        })
        .catch((error: Error) => {
          console.error(error);
        });
    }
  };

  const onCloseHandler = () => {
    onChange?.(undefined);
    fileUploadRef.current?.resetFiles();
    resetToDefault();
    onClose?.();
  };

  const onCropHandler = (preview?: string) => {
    dispatch({ type: AvatarActionType.SetPreview, preview });
  };

  useEffect(() => {
    if (state.preview) {
      if (!initialRender.current) {
        onCrop?.(state.preview);
        onChange?.(state.preview);
      } else {
        initialRender.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Callbacks intentionally excluded to prevent re-triggering on callback identity changes
  }, [state.preview]);

  return (
    <div className="odin-mb-2 odin-flex odin-flex-col odin-items-center odin-gap-4">
      <FileUpload
        allowedFileTypes={['jpeg', 'jpg', 'png']}
        maxUploadFileSize={maxFileSize}
        maxUploadFiles={1}
        onChange={onFilesChanged}
        error={state.errorState}
        ref={fileUploadRef}
      >
        {({ dropElement }) => (
          <div
            className="flex w-full flex-col items-center justify-center odin-group odin-min-h-[200px] odin-min-w-min"
            ref={dropElement}
          >
            <div className="relative rounded-full overflow-hidden border odin-border-primary-2">
              <img id={name} width={200} src={state.preview} onError={resetToDefault} alt="profile image" />
            </div>

            <span className="mt-2 odin-opacity-0 transition-opacity duration-300 group-hover:odin-opacity-100">
              {t('file-upload')}
            </span>
          </div>
        )}
      </FileUpload>

      {!readOnly && state.src && (
        <AvatarEditor
          width={0}
          height={height}
          onEdit={onCropHandler}
          onRemove={onCloseHandler}
          source={state.src}
          shadingOpacity={0.4}
        />
      )}
      {state.errorState && <ErrorLabel>{state.errorState}</ErrorLabel>}
    </div>
  );
}

const fileToBase64 = (file: File): Promise<string | undefined> => {
  return new Promise<string | undefined>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const base64String = reader.result?.toString() || undefined;
      resolve(base64String);
    };

    reader.onerror = () => {
      reader.abort();
      reject(new Error('Error reading file.'));
    };

    reader.readAsDataURL(file);
  });
};
